import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { formatInvoiceAmount } from './invoiceTabStyles'

const invoiceTabSectionCardClass =
  'flex min-w-[240px] flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-card shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

export function InvoiceTabSectionHeading({
  title,
  icon,
  action,
}: {
  title: string
  icon?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className='flex h-16 items-center gap-2 overflow-hidden px-6 pb-2 pt-6'>
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        {icon ? (
          <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#ecfdf5] text-[#059669]'>
            {icon}
          </span>
        ) : null}
        <h2 className='truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
          {title}
        </h2>
      </div>
      {action ? <div className='shrink-0'>{action}</div> : null}
    </div>
  )
}

export function InvoiceTabSectionCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn(invoiceTabSectionCardClass, className)}>
      {children}
    </section>
  )
}

export function InvoiceDetailRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className='flex h-8 items-center justify-between gap-2 border-b border-border pb-2'>
      <span className='w-40 shrink-0 text-sm text-[#757575]'>{label}</span>
      <div className='flex min-w-0 flex-1 items-center justify-end text-sm'>
        {children}
      </div>
    </div>
  )
}

export function InvoiceLineItemsTotalsFooter({
  subtotal,
  taxTotal,
  total,
  labels,
}: {
  subtotal: number
  taxTotal: number
  total: number
  labels: { subtotal: string; tax: string; total: string }
}) {
  const rows = [
    { label: labels.subtotal, value: subtotal, bold: false },
    { label: labels.tax, value: taxTotal, bold: false },
    { label: labels.total, value: total, bold: true },
  ]

  return (
    <div className='flex w-full justify-end overflow-hidden border-y border-border'>
      <div className='flex min-w-0 flex-1 flex-col'>
        {rows.map((row, i) => (
          <div
            key={row.label}
            className={cn(
              'flex items-center justify-end bg-[#f9f9f9] px-3',
              row.bold ? 'h-14 py-4' : 'h-12',
              i > 0 && 'border-b border-border'
            )}
          >
            <span
              className={cn(
                'text-sm font-semibold',
                row.bold ? 'text-base text-foreground' : 'text-[#757575]'
              )}
            >
              {row.label}
            </span>
          </div>
        ))}
      </div>
      <div className='flex w-[200px] shrink-0 flex-col'>
        {rows.map((row, i) => (
          <div
            key={`${row.label}-value`}
            className={cn(
              'flex items-center justify-end bg-[#f9f9f9] pl-3 pr-14',
              row.bold ? 'h-14 py-4' : 'h-12',
              i > 0 && 'border-b border-border'
            )}
          >
            <span
              className={cn('text-sm', row.bold && 'text-base font-semibold')}
            >
              {formatInvoiceAmount(row.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
